import * as React from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { useQuery } from "react-query";
import {
  JobRequestFileCategory,
  JobRequestNewFile,
  LltsLanguage,
  MachineTranslationService,
  UtilsService
} from "gen/clients/llts";
import { SelectOption } from "@mui/base";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";
import { useS3FileUpload } from "hooks/useS3FileUpload/useS3FileUpload2";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { DropzoneField } from "components/formikFields/DropzoneField/DropzoneField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import { AlertDialog } from "components/AlertDialog/AlertDialog";
import { LinearProgressWithLabel } from "components/LinearProgressWithLabel/LinearProgressWithLabel";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { ErrorPage } from "components/ErrorPage/ErrorPage";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

export enum FieldName {
  projectName = "projectName",
  sourceLanguage = "sourceLanguage",
  targetLanguage = "targetLanguage",
  sourceFiles = "sourceFiles",
  translatedFiles = "translatedFiles"
}

export interface FormValues {
  [FieldName.projectName]: string;
  [FieldName.sourceLanguage]: SelectOption<string> | null;
  [FieldName.targetLanguage]: SelectOption<string> | null;
  [FieldName.sourceFiles]: File[];
  [FieldName.translatedFiles]: File[];
}

const MAX_TOTAL_FILES_SIZE_BYTES = 250 * 1024 * 1024; // 250 MB

const ACCEPTED_FILE_TYPES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/pdf", // .pdf
  ".docx",
  ".pptx",
  ".xlsx",
  ".pdf"
];

const ImproveTranslationStep: React.FC<Props> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    invoke: uploadFiles,
    isUploading: areFilesUploading,
    error: filesUploadError,
    uploadProgress,
    reset: resetS3FileUpload
  } = useS3FileUpload();

  const {
    data: mtSettings,
    isLoading: areMtSettingsLoading,
    error: mtSettingsError
  } = useQuery(["getMtSettings"], {
    queryFn: MachineTranslationService.getMachineTranslationSettings
  });

  const {
    data: lltsLanguages,
    isLoading: areLltsLanguagesLoading,
    error: lltsLanguagesError
  } = useQuery(["listLltsLanguages"], {
    queryFn: () => UtilsService.listLltsLanguages({})
  });

  const isLoading = areMtSettingsLoading || areLltsLanguagesLoading;
  const error = mtSettingsError || lltsLanguagesError;

  const lltsLanguageById: Record<string, LltsLanguage> = React.useMemo(
    () =>
      (lltsLanguages || []).reduce(
        (prevValue, currentValue) => {
          // eslint-disable-next-line no-param-reassign
          prevValue[currentValue.id] = currentValue;
          return prevValue;
        },
        {} as Record<string, LltsLanguage>
      ),
    [lltsLanguages]
  );

  const languageOptions = React.useMemo(
    () =>
      mtSettings?.supportedLltsLanguages
        ?.filter(langId => lltsLanguageById[langId] && !lltsLanguageById[langId].isDisabled)
        .map(langId => ({
          label: lltsLanguageById[langId]?.name || langId,
          value: langId
        }))
        .sort(SELECT_OPTION_COMPARATOR) || [],
    [mtSettings, lltsLanguageById]
  );

  const initialValues: FormValues = {
    [FieldName.projectName]: "",
    [FieldName.sourceLanguage]: null,
    [FieldName.targetLanguage]: null,
    [FieldName.sourceFiles]: [],
    [FieldName.translatedFiles]: []
  };

  /**
   * Extracts the base name from a file name by removing any language code suffix before the extension.
   * For example: "document_ua.docx" -> "document.docx", "file.pdf" -> "file.pdf"
   */
  const getBaseFileName = React.useCallback((fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) {
      // No extension, return as is
      return fileName;
    }

    const nameWithoutExtension = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    // Check if the name ends with an underscore followed by a language code (e.g., _ua, _en, _fr-CA)
    // Language codes can be 2-3 letters, optionally followed by a hyphen and 2-3 more letters
    const languageSuffixPattern = /_[a-zA-Z]{2,3}(-[a-zA-Z]{2,3})?$/;
    const baseNameWithoutSuffix = nameWithoutExtension.replace(languageSuffixPattern, "");

    return baseNameWithoutSuffix + extension;
  }, []);

  const validate = React.useCallback(
    (values: FormValues) => {
      const errors: Partial<Record<FieldName, string>> = {};

      // Validate required files
      if (values[FieldName.sourceFiles].length === 0) {
        errors[FieldName.sourceFiles] = t("common.validation.required");
      }
      if (values[FieldName.translatedFiles].length === 0) {
        errors[FieldName.translatedFiles] = t("common.validation.required");
      }

      const allFiles = [...values[FieldName.sourceFiles], ...values[FieldName.translatedFiles]];
      const totalFilesSize = allFiles.reduce((prev, currentFile) => prev + currentFile.size, 0);
      if (totalFilesSize >= MAX_TOTAL_FILES_SIZE_BYTES) {
        errors[FieldName.sourceFiles] = t("machineTranslation.aiToolbox.validation.maxTotalFilesSize");
      }

      // Validate that the number of source and translated files match
      const sourceFilesCount = values[FieldName.sourceFiles].length;
      const translatedFilesCount = values[FieldName.translatedFiles].length;

      if (sourceFilesCount > 0 && translatedFilesCount > 0 && sourceFilesCount !== translatedFilesCount) {
        errors[FieldName.translatedFiles] = t("machineTranslation.aiToolbox.validation.fileCountMismatch", {
          sourceCount: sourceFilesCount,
          translatedCount: translatedFilesCount
        });
      }

      // Validate that every source file has a matching translated file
      if (sourceFilesCount > 0 && translatedFilesCount > 0 && sourceFilesCount === translatedFilesCount) {
        const translatedBaseNames = new Set(values[FieldName.translatedFiles].map(file => getBaseFileName(file.name)));

        const unmatchedSourceFiles = values[FieldName.sourceFiles].filter(
          sourceFile => !translatedBaseNames.has(sourceFile.name)
        );

        if (unmatchedSourceFiles.length > 0) {
          const unmatchedFileNames = unmatchedSourceFiles.map(f => f.name).join(", ");
          errors[FieldName.translatedFiles] = t("machineTranslation.aiToolbox.validation.unmatchedSourceFiles", {
            fileNames: unmatchedFileNames
          });
        }
      }

      return errors;
    },
    [t, getBaseFileName]
  );

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      resetS3FileUpload();
      const allFiles = [...values[FieldName.sourceFiles], ...values[FieldName.translatedFiles]];

      await uploadFiles({
        files: allFiles,
        onSuccess: async fileKeys => {
          const sourceFileKeys = fileKeys.slice(0, values[FieldName.sourceFiles].length);
          const translatedFileKeys = fileKeys.slice(values[FieldName.sourceFiles].length);

          const jobRequestFiles: JobRequestNewFile[] = [
            ...values[FieldName.sourceFiles].map((file, index) => ({
              fileName: file.name,
              category: JobRequestFileCategory.SOURCE_DOCUMENT,
              fileKey: sourceFileKeys[index]
            })),
            ...values[FieldName.translatedFiles].map((file, index) => ({
              fileName: file.name,
              category: JobRequestFileCategory.TRANSLATED_DOCUMENT,
              fileKey: translatedFileKeys[index],
              languageId: values[FieldName.targetLanguage]!.value
            }))
          ];

          await MachineTranslationService.postEditFiles({
            requestBody: {
              requestName: values[FieldName.projectName],
              sourceLanguageId: values[FieldName.sourceLanguage]!.value,
              targetLanguageIds: [values[FieldName.targetLanguage]!.value],
              files: jobRequestFiles
            }
          });

          onSuccess();
        }
      });
    },
    [onSuccess, resetS3FileUpload, uploadFiles]
  );

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage apiError={error} />;
  }

  return (
    <>
      {filesUploadError && <SnackbarApiError error={filesUploadError} />}
      <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
        {({ isSubmitting }) => (
          <Form noValidate={true} autoComplete="off" autoCorrect="off">
            <Stack spacing={3}>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                {t("machineTranslation.aiToolbox.actions.improveTranslation.panelDescription")}
              </Typography>

              <TextInputField
                name={FieldName.projectName}
                label={t("machineTranslation.aiToolbox.projectName.label")}
                helperText={t("machineTranslation.aiToolbox.projectName.helperText")}
                required={true}
                preventCharacters={[";"]}
                preventCharactersMessage={t("common.validation.semicolonNotAllowed")}
                maxLength={140}
                maxLengthMessage={t("common.validation.projectNameMaxLength")}
              />

              <SelectOneAutocompleteField
                name={FieldName.sourceLanguage}
                label={t("machineTranslation.aiToolbox.sourceLanguage.label")}
                helperText={t("machineTranslation.aiToolbox.sourceLanguage.helperText")}
                options={languageOptions}
                required={true}
              />

              <SelectOneAutocompleteField
                name={FieldName.targetLanguage}
                label={t("machineTranslation.aiToolbox.targetLanguage.label")}
                helperText={t("machineTranslation.aiToolbox.targetLanguage.helperText")}
                options={languageOptions}
                required={true}
              />

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  {t("machineTranslation.aiToolbox.sourceFiles.label")}
                </Typography>
                <DropzoneField
                  name={FieldName.sourceFiles}
                  dropzoneText={t("machineTranslation.aiToolbox.sourceFiles.dropzoneText")}
                  helperText={t("machineTranslation.aiToolbox.sourceFiles.helperText")}
                  fileUploadLimits={{
                    maxFileCount: mtSettings?.maxFilesPerRequest,
                    maxFileSize: mtSettings ? mtSettings.maxFileSizeMB * 1024 * 1024 : undefined,
                    acceptedFiles: ACCEPTED_FILE_TYPES
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  {t("machineTranslation.aiToolbox.translatedFiles.label")}
                </Typography>
                <DropzoneField
                  name={FieldName.translatedFiles}
                  dropzoneText={t("machineTranslation.aiToolbox.translatedFiles.dropzoneText")}
                  helperText={t("machineTranslation.aiToolbox.translatedFiles.helperText")}
                  fileUploadLimits={{
                    maxFileCount: mtSettings?.maxFilesPerRequest,
                    maxFileSize: mtSettings ? mtSettings.maxFileSizeMB * 1024 * 1024 : undefined,
                    acceptedFiles: ACCEPTED_FILE_TYPES
                  }}
                />
              </Box>

              <Stack direction="row" spacing={1} justifyContent="center" pt={5}>
                <LoadingButton isLoading={isSubmitting}>{t("machineTranslation.aiToolbox.submitBtn")}</LoadingButton>
                <Button onClick={onClose}>{t("common.cancel")}</Button>
              </Stack>

              <Box pt={4}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.grey["700"] }}
                  dangerouslySetInnerHTML={{ __html: t("machineTranslation.aiToolbox.disclaimer") }}
                />
              </Box>
            </Stack>
          </Form>
        )}
      </Formik>

      {areFilesUploading && (
        <AlertDialog title={t("machineTranslation.aiToolbox.uploadingAlert.title")}>
          <Stack spacing={1}>
            <Box>{t("machineTranslation.aiToolbox.uploadingAlert.message")}</Box>
            <Box sx={{ width: "100%" }}>
              <LinearProgressWithLabel value={uploadProgress} />
            </Box>
          </Stack>
        </AlertDialog>
      )}
    </>
  );
};

export { ImproveTranslationStep };
